import type { NBAItem } from "../types";
import styles from "./ActionItem.module.css";

/** `type` of a row written by a skill's Notify node (aiva_log_action). */
export const SKILL_ACTION_TYPE = "skill_action";

interface ActionItemProps {
  readonly action: NBAItem;
  readonly onToggle: () => void;
}

/** 10px clock for the due chip and warning triangle for an overdue one. */
function ClockIcon() {
  return (
    <svg className={styles.chipIcon} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 4.5V8l2.5 1.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg className={styles.chipIcon} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M8 2.5 14 13H2L8 2.5z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M8 6.5V9.5M8 11.5v.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/** "Due 31 Oct" for a parseable ISO date; null otherwise so nothing is shown. */
export function formatDue(dueAt: string | null | undefined): string | null {
  if (!dueAt) return null;
  const ms = Date.parse(dueAt);
  if (Number.isNaN(ms)) return null;
  // British day-month ("Due 31 Oct"), whatever the host locale.
  return `Due ${new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`;
}

const DAY_MS = 86_400_000;

/** Whole calendar days a due date is past, in local time; 0 when it is today,
 *  in the future, or unparseable. A row due today is not overdue until tomorrow. */
export function overdueDays(dueAt: string | null | undefined, now: number = Date.now()): number {
  if (!dueAt) return 0;
  const due = new Date(Date.parse(dueAt));
  if (Number.isNaN(due.getTime())) return 0;
  const today = new Date(now);
  const dueDay = Date.UTC(due.getFullYear(), due.getMonth(), due.getDate());
  const todayDay = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.max(0, Math.round((todayDay - dueDay) / DAY_MS));
}

/** A to-do row. The checkbox is its only control: anything a skill wants the
 *  user to act on belongs on a card, not here. */
export function ActionItem({ action, onToggle }: Readonly<ActionItemProps>) {
  const due = formatDue(action.due_at);
  const overdue = action.is_completed ? 0 : overdueDays(action.due_at);

  return (
    <div className={`${styles.actionItem} ${action.is_completed ? styles.done : ""}`}>
      <input type="checkbox" className={styles.checkbox} checked={action.is_completed} onChange={onToggle} />
      <div className={styles.body}>
        <span className={styles.label}>{action.title}</span>
        <span className={styles.meta}>
          <span className={styles.service}>{action.service}</span>
          {due && <span className={styles.metaSep} aria-hidden="true">·</span>}
          {due && overdue > 0 && (
            <span className={`${styles.due} ${styles.overdue}`} title={due}>
              <WarningIcon />
              Overdue · {overdue}d
            </span>
          )}
          {due && overdue === 0 && (
            <span className={styles.due}>
              <ClockIcon />
              {due}
            </span>
          )}
        </span>
      </div>
    </div>
  );
}
